import React, { useRef, useState } from "react";
import styled from "styled-components";
import { Toast } from "primereact/toast";
import { CoverRequirementsModal } from "components/Labels/LabelDetail/CoverRequirementsModal";
import {PreviewModal} from "components/Labels/LabelDetail/PreviewModal";
import {DeletePreviewImageModal} from "components/Labels/LabelDetail/DeletePreviewImageModal";
import {FormattedMessage} from "react-intl";

interface ImageProps {
  src?: string;
  onSave: (data: any) => Promise<void>;
}

export const PreviewImage: React.FC<ImageProps> = props => {
  const { src, onSave } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const [isOpenCoverRequirementsModal, setIsOpenCoverRequirementsModal] = useState(false);
  const [isOpenPreviewModal, setIsOpenPreviewModal] = useState(false);
  const [isOpenDeletePreviewImageModal, setIsOpenDeletePreviewImageModal] = useState(false);
  const [preview, setPreview] = useState<string | null | undefined>(src || null || undefined);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png'];
    if (!allowed.includes(file.type)) {
      toast.current?.show({
        severity: 'error',
        summary: 'Invalid Format',
        detail: 'Only JPEG or PNG files are allowed',
        life: 4000,
      });
      setPreview(null);
      e.target.value = ''
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.src = url;

    img.onload = async () => {
      if (img.width !== 500 || img.height !== 500 || img.width !== img.height) {
        toast.current?.show({
          severity: 'error',
          summary: 'Invalid Dimensions',
          detail: 'Image must be at least 500x500px and square',
          life: 4000,
        });
        URL.revokeObjectURL(url);
        src ? setPreview(src) : setPreview(null);
        e.target.value = ''
        return;
      }
      setPreview(url)

      await onSave({ cover: file, remove_cover: 0 });
      setIsOpenCoverRequirementsModal(false);
    };

    img.onerror = () => {
      toast.current?.show({
        severity: 'error',
        summary: 'Load Error',
        detail: 'Unable to load image',
        life: 4000,
      });
      URL.revokeObjectURL(url);
      setPreview(null);
      e.target.value = ''
    };
  };

  const onOpenFileUpload = () => {
    inputRef.current?.click();
  };

  const onDownloadImage = (url?: string) => {
    if (!url) return;
    const extension = url.split('.').pop()?.split('?')[0] || 'jpg';
    const a = document.createElement('a');
    a.href = `/api/download?url=${encodeURIComponent(url)}`;
    a.download = `image.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onDelete = async () => {
    try {
      await onSave({ remove_cover: 1 });
      setPreview(null);
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  return (
    <Container>
      <Toast ref={toast} />
      {preview ? (
        <>
          <PreviewSettings>
            <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="10.5" cy="10.5" r="10.5" fill="white"/>
              <circle cx="10.5" cy="5.5" r="1.5" fill="black"/>
              <circle cx="10.5" cy="10.5" r="1.5" fill="black"/>
              <circle cx="10.5" cy="15.5" r="1.5" fill="black"/>
            </svg>
            <Menu>
              <MenuItem onClick={() => onOpenFileUpload()}>
                <FileInput
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <FormattedMessage
                  id="Labels.PreviewImage.ReplaceCoverArt"
                  defaultMessage="Replace cover art"
                />
              </MenuItem>
              <MenuItem onClick={()=> setIsOpenPreviewModal(true)}>
                Preview cover art
              </MenuItem>
              <MenuItem onClick={() => onDownloadImage(preview)}>
                <FormattedMessage
                  id="Labels.PreviewImage.DownloadCoverArt"
                  defaultMessage="Download cover art"
                />
              </MenuItem>
              <MenuItem onClick={() => setIsOpenDeletePreviewImageModal(true)}>
                <FormattedMessage
                  id="Labels.PreviewImage.DownloadCoverArt"
                  defaultMessage="Delete cover art"
                />
              </MenuItem>
            </Menu>
          </PreviewSettings>
          <Preview src={preview} alt="Preview" />
        </>
      ) : (
        <AddImageContainer onClick={() => setIsOpenCoverRequirementsModal(true)}>
          <AddImageLabel>
            <span>
              +
              <FormattedMessage
                id="Labels.PreviewImage.AddCoverArt"
                defaultMessage="Add cover art"
              />
            </span>
            <FileInput
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
          </AddImageLabel>
        </AddImageContainer>
      )}
      <CoverRequirementsModal
        isOpen={isOpenCoverRequirementsModal}
        onClose={() => setIsOpenCoverRequirementsModal(false)}
        toast={toast}
        onOpenFileUpload={onOpenFileUpload}
      />
      {!!preview && (
        <PreviewModal
          preview={preview}
          isOpen={isOpenPreviewModal}
          onClose={() => setIsOpenPreviewModal(false)}
        />
      )}
      {!!preview && (
        <DeletePreviewImageModal
          onDelete={onDelete}
          isOpen={isOpenDeletePreviewImageModal}
          onClose={() => setIsOpenDeletePreviewImageModal(false)}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  width: 133px;
  height: 133px;
  border-radius: 15px;
  background-color: #E9E9E9;
`;

export const AddImageContainer = styled.div`
  width: 100%;
  height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

export const AddImageLabel = styled.div`
  width: 101px;
  height: 33px;
  border-radius: 7px;
  background: #FFFFFF;
  font-family: SF Pro Display;
  font-weight: 500;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 18px;
  letter-spacing: 0;
  text-align: center;
  vertical-align: middle;
  color:#000000;
  display:flex;
  align-items: center;
  justify-content: center;
`;

export const MenuItem = styled.div`
  width: 160px;
  height: 39px;
  border-radius: 10px;
  padding: 10px 8px 11px;

  &:hover {
    background: #E2E5FF;
    cursor:pointer;
  }
`;

export const Menu = styled.div`
  position: absolute;
  top: 20px;
  left: 0;
  display: none;
  width: 182px;
  height: 182px;
  border-radius: 10px;
  white-space: nowrap;
  z-index: 9999999;
  background: #FFFFFF;
  padding: 15px 11px;
  box-shadow: 0 0 10px 3px #BCBCBFAD;
`;

export const PreviewSettings =  styled.div`
  position: absolute;
  bottom: 9px;   
  right: 12px;   
  width: 21px;
  height: 21px;
  border-radius:50%;
  background:#FFFFFF;
  z-index: 9;
  cursor:pointer;

  &:hover ${Menu} {
      display: flex;
      flex-direction: column;
      padding-top: 13px;
    }
`;

export const FileInput = styled.input`
  display: none;
`;

export const Preview = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 15px;
`;
